import { Spacing, Text } from '@vkontakte/vkui';
import image from 'img/quota-landing/autoupload-onboading-2.png';
import imageMobile from 'img/quota-landing/autoupload-onboading-mobile-2.png';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PROMO_TARIFFS } from 'reactApp/appHelpers/configHelpers';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { helpAutoUploadHref } from 'reactApp/appHelpers/featuresHelpers/features/autoUploadPromo';
import { WhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { QuotaLandingSelectors } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './AutoUploadOnboardingFinalDialog.css';

const sendRadar = (action: 'click' | 'show' | 'close', data?: Record<string, string>) => {
    sendPaymentGa({
        eventCategory: ECategoryGa.modal,
        action,
        name: 'mnz_autoload_1tb_v2_p2',
        ...(data && { ...data }),
    });
};

export const AutoUploadOnboardingFinalDialog = memo<{ onClose?(): void }>(({ onClose }) => {
    const dispatch = useDispatch();
    const scrollRef = useRef<HTMLDivElement>(null);
    const isMobile = useSelector(QuotaLandingSelectors.isMobile);

    const handleClick = useCallback(() => {
        dispatch(
            openTariffBuy({
                productId: PROMO_TARIFFS.autouploadPromoTariff,
                checkAnonymous: false,
                isMobile,
                paySource: 'autoload_1tb_v2',
                title: !isMobile ? 'Тариф 1 ТБ и опция безлимит' : undefined,
                hideOtherTariffs: true,
            })
        );
        sendRadar('click', { button_name: 'buy_subscription' });
        emitAnalyticEvent(AnalyticEventNames.AUTOUPLOAD_ONBOARDING_FINAL_CLICK);
        onClose?.();
    }, [onClose, isMobile, dispatch]);

    const handleClose = useCallback(() => {
        sendRadar('close');
        onClose?.();
    }, [onClose]);

    useEffect(() => {
        sendRadar('show');
        emitAnalyticEvent(AnalyticEventNames.AUTOUPLOAD_ONBOARDING_FINAL_SHOW);
    }, []);

    const content = useMemo(() => {
        return (
            <>
                <Spacing size={16} />
                <Text className={styles.text}>
                    Называется: безлимитная автозагрузка или просто — безлимит. Выберите тариф подписки и у вас всегда будет свободная
                    память на телефоне. Радуемся за вас!
                </Text>
                <a className={styles.link} href={helpAutoUploadHref} target="_blank" rel="noreferrer">
                    Подробные условия использования
                </a>
                <Spacing size={36} />
            </>
        );
    }, []);

    if (isMobile) {
        return (
            <MobileDialog
                id="autoupload-onboading-final-mobile"
                onClose={handleClose}
                topmost
                closeOnDimmerClick
                scrollRef={scrollRef}
                mod="base"
                contextPadding="zero"
                isRebrandingQuotaLanding
            >
                <div ref={scrollRef}>
                    <img src={imageMobile} alt="Эта крутая возможность с подпиской Mail Space" className={styles.mobileImg} />
                    <Spacing size={10} />
                    <Text className={styles.mobileTitle}>Эта крутая возможность с&nbsp;подпиской Mail Space</Text>
                    <Spacing size={8} />
                    <Text className={styles.mobileText}>
                        Выберите тариф и у вас всегда будет свободная память на&nbsp;телефоне и&nbsp;бесконечное&nbsp;Облако. Радуемся за
                        вас!
                    </Text>
                    <a className={styles.mobileLink} href={helpAutoUploadHref} target="_blank" rel="noreferrer">
                        Подробные условия использования
                    </a>
                    <Spacing size={24} />
                    <div className={styles.buttonWrapper}>
                        <Button primary theme="vk" sizeMode={ButtonSizeMode.extraBig} onClick={handleClick} className={styles.button}>
                            Попробовать
                        </Button>
                    </div>
                    <Spacing size={16} />
                </div>
            </MobileDialog>
        );
    }

    return (
        <WhatsNewDialog
            imageUrl={image}
            title="Эта крутая возможность с Mail Space "
            content={content}
            primaryButtonText="Попробовать"
            onClick={handleClick}
            onClose={handleClose}
            imageBgColor="var(--rebranding--dark_theme_color_background_sky)"
            buttonTheme="vk"
            dialogSize="tiny"
            qaId="autoupload-onboading-final"
            isRebranding
        />
    );
});

AutoUploadOnboardingFinalDialog.displayName = 'AutoUploadOnboardingFinalDialog';
